/**
 * @file matchScoring.js
 * @description Atomic math helpers used by the applicantFitEngine
 *   (and a small handful of discovery-specific paths that need raw
 *   primitives without going through the engine's dimension assessors).
 *   Each helper does one tiny job and has no concept of dimension weights —
 *   that's the caller's responsibility.
 *
 *   Today the engine (`lib/applicantFitEngine.js`) is the canonical fit
 *   scorer; it consumes these primitives via its per-dimension assessors
 *   (assessSkills, assessSetting, assessSchedule, …). The engine's weight
 *   profiles (AGENCY_REVIEW, MARKETPLACE, DISCOVERY_RBT, DISCOVERY_BCBA)
 *   decide how to combine the dimensions for each surface.
 *
 *   These helpers stay separate from the engine because:
 *     - discoveryFit needs `distanceFitScore` (soft fall-off) which the
 *       engine doesn't model — engine distance is binary.
 *     - RBTProfile/discoveryFit need raw `haversineMiles` for soft distance.
 *     - The engine itself imports a subset (`settingsOverlap`,
 *       `scheduleOverlapFraction`, `withinDistance`, `ageGroupOverlapFraction`)
 *       so the math lives in exactly one place.
 *
 *   Convention:
 *     - Helpers return `null` when the dimension can't be evaluated (e.g.,
 *       case doesn't specify any required skills). Callers decide whether
 *       null means "skip the dimension" or "0 score against denominator".
 *     - Fraction helpers return an UNROUNDED 0..1 number. Callers multiply
 *       by their weight and round at the level they want.
 *     - Boolean helpers return true/false (overlap yes/no, in-range yes/no).
 *
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/matchScoring
 */

/**
 * Returns the fraction (0..1) of required skills the candidate has.
 * Returns null when there are no required skills (dimension not applicable).
 *
 * The fraction is intentionally unrounded so each caller can control its own
 * rounding behavior. The engine rounds the final overall score; some legacy
 * scorers rounded partial products instead.
 *
 * @param {string[]|null|undefined} requiredSkills
 * @param {string[]|null|undefined} candidateSkills
 * @returns {number|null} 0..1 fraction, or null if no required skills
 */
export function skillsOverlapFraction(requiredSkills, candidateSkills) {
  const required = requiredSkills || [];
  if (required.length === 0) return null;
  const candidate = candidateSkills || [];
  const matched = required.filter(s => candidate.includes(s)).length;
  return matched / required.length;
}

/**
 * Returns true if any required setting overlaps the candidate's settings.
 * Returns null when there are no required settings.
 *
 * Note: this helper does NOT special-case an empty candidate array. If the
 * candidate has no settings, the result is `false` (no overlap). Callers
 * that want to SKIP the dimension when the candidate has none (rather than
 * count it as a non-match) should add their own length check around this
 * call — see the engine's MARKETPLACE profile, which uses scoreDimensions.setting
 * to return null (excluding the dimension from the denominator) in that case.
 *
 * @param {string[]|null|undefined} requiredSettings
 * @param {string[]|null|undefined} candidateSettings
 * @returns {boolean|null} true/false overlap, or null if no required settings
 */
export function settingsOverlap(requiredSettings, candidateSettings) {
  const required = requiredSettings || [];
  if (required.length === 0) return null;
  const candidate = candidateSettings || [];
  return required.some(s => candidate.includes(s));
}

/**
 * Returns the fraction (0..1) of required days the candidate is available.
 * Returns null when there are no required days.
 *
 * @param {string[]|null|undefined} requiredDays
 * @param {string[]|null|undefined} candidateDays
 * @returns {number|null} 0..1 fraction, or null if no required days
 */
export function scheduleOverlapFraction(requiredDays, candidateDays) {
  const required = requiredDays || [];
  if (required.length === 0) return null;
  const candidate = candidateDays || [];
  const overlap = required.filter(d => candidate.includes(d)).length;
  return overlap / required.length;
}

/**
 * Returns true if the candidate's distance to the case is within the limit.
 * Returns null when either bound is missing — caller decides whether to
 * skip the dimension or treat the missing value as a non-match.
 *
 * @param {number|null|undefined} candidateDistanceMi
 * @param {number|null|undefined} maxDistanceMi
 * @returns {boolean|null}
 */
export function withinDistance(candidateDistanceMi, maxDistanceMi) {
  if (candidateDistanceMi == null || maxDistanceMi == null) return null;
  return candidateDistanceMi <= maxDistanceMi;
}

// ─────────────────────────────────────────────────────────────────────────────
// Discovery-fit helpers
// ─────────────────────────────────────────────────────────────────────────────
// These power the therapist-facing card grade in MapView/FindCasesV2 via
// `discoveryFit.computeFit`. They live here (not inside that module) because
// the math primitives are reusable and we want a single place to fix bugs in
// e.g. age-range parsing or distance fall-off.

/**
 * Parses a free-form age range string into a numeric { min, max } window.
 * Accepts: "3–8", "3-8", "3 to 8", "5", "6-12 yrs", "Ages 4–7".
 * Single ages widen by ±1 ("5" → 4..6) to mirror caseService's display rule.
 * Returns null when no numbers can be extracted.
 *
 * @param {string|null|undefined} rangeStr
 * @returns {{min: number, max: number}|null}
 */
export function parseAgeRange(rangeStr) {
  if (!rangeStr) return null;
  const s = String(rangeStr);
  // Capture all numeric tokens, ignoring "yrs", "Ages", separators, etc.
  const nums = s.match(/\d+/g);
  if (!nums || nums.length === 0) return null;
  const a = parseInt(nums[0], 10);
  if (isNaN(a)) return null;
  if (nums.length === 1) {
    return { min: Math.max(0, a - 1), max: a + 1 };
  }
  const b = parseInt(nums[1], 10);
  if (isNaN(b)) return { min: Math.max(0, a - 1), max: a + 1 };
  return { min: Math.min(a, b), max: Math.max(a, b) };
}

/** Maps the 5 onboarding age-group labels to numeric windows. */
const AGE_GROUP_WINDOWS = {
  "2–5":   { min: 2,  max: 5  },
  "2-5":   { min: 2,  max: 5  },
  "6–10":  { min: 6,  max: 10 },
  "6-10":  { min: 6,  max: 10 },
  "11–14": { min: 11, max: 14 },
  "11-14": { min: 11, max: 14 },
  "15–18": { min: 15, max: 18 },
  "15-18": { min: 15, max: 18 },
  "Adults": { min: 18, max: 99 },
};

/**
 * Returns the fraction (0..1) of the therapist's preferred age groups that
 * overlap the case's client age range. Null when no useful data on either side.
 *
 * The case stores a free-form range ("3–8"); the therapist stores categorical
 * buckets (["2–5","6–10",...]). This bridges the two by parsing the range and
 * checking numeric overlap against each bucket window.
 *
 * Scoring rationale: a therapist who picked "2–5" and "6–10" against a "3–8"
 * case is fully covered (both buckets touch the range) → 1.0. A therapist who
 * picked only "2–5" against "10–14" → 0.0 (no overlap). Partial coverage
 * (some buckets overlap, others don't) returns the matching fraction.
 *
 * @param {string|null|undefined} caseAgeRange
 * @param {string[]|null|undefined} therapistAgeGroups
 * @returns {number|null}
 */
export function ageGroupOverlapFraction(caseAgeRange, therapistAgeGroups) {
  const range = parseAgeRange(caseAgeRange);
  const groups = therapistAgeGroups || [];
  if (!range || groups.length === 0) return null;
  const matched = groups.filter(label => {
    const w = AGE_GROUP_WINDOWS[label];
    if (!w) return false;
    // Numeric range overlap: max >= range.min && min <= range.max
    return w.max >= range.min && w.min <= range.max;
  }).length;
  return matched / groups.length;
}

/**
 * BCaBA-only fit score for the case's supervision arrangement vs. the
 * therapist's existing supervision setup. Returns 0..1 or null when not
 * applicable (non-BCaBA case, or no data on either side).
 *
 * Logic:
 *   - case "provided"  → 1.0 (agency supplies BCBA oversight; always works)
 *   - case "flexible"  → 1.0 (works either way)
 *   - case "required"  → 1.0 if therapist has an arranged supervisor (supervisorName set)
 *                      → 0.0 otherwise (therapist needs to bring their own and hasn't)
 *
 * @param {string|null|undefined} caseSupervision  "provided"|"required"|"flexible"
 * @param {Object|null|undefined} therapistBcabaDetails
 * @returns {number|null}
 */
export function bcabaSupervisionFitScore(caseSupervision, therapistBcabaDetails) {
  if (!caseSupervision) return null;
  const v = String(caseSupervision).toLowerCase();
  if (v === "provided" || v === "flexible") return 1;
  if (v === "required") {
    const details = therapistBcabaDetails || {};
    const hasSupervisor = !!(details.supervisorName && String(details.supervisorName).trim());
    return hasSupervisor ? 1 : 0;
  }
  return null;
}

/**
 * Soft distance fit score on 0..1 with capped fall-off. Returns null when
 * either bound is missing so callers null-skip the dimension.
 *
 *   distanceMi <= maxDistanceMi             → 1.0  (well within stated range)
 *   distanceMi between 1× and 2× of max     → linear fall-off from 1.0 → 0.0
 *   distanceMi >= 2× max                    → 0.0
 *
 * Caller multiplies by its own (small) weight so the *grade* never collapses
 * solely due to distance — that's by design. Distance also surfaces as an
 * annotation chip on the card so the therapist sees the tradeoff explicitly.
 *
 * @param {number|null|undefined} distanceMi
 * @param {number|null|undefined} maxDistanceMi
 * @returns {number|null}
 */
export function distanceFitScore(distanceMi, maxDistanceMi) {
  if (distanceMi == null || maxDistanceMi == null) return null;
  const max = Number(maxDistanceMi);
  const d   = Number(distanceMi);
  if (!isFinite(max) || max <= 0 || !isFinite(d)) return null;
  if (d <= max) return 1;
  const over = (d - max) / max; // 0 at the boundary, 1 at 2× max
  return Math.max(0, 1 - over);
}

/**
 * Great-circle distance in miles between two coordinates. Mirrors the
 * `haversine` exported from marketReach so discoveryFit doesn't have to
 * import a heavier analytics module just to do the math.
 *
 * @param {number} lat1 @param {number} lng1
 * @param {number} lat2 @param {number} lng2
 * @returns {number} miles
 */
export function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
